import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Form, Input, Button, Select, DatePicker, AutoComplete } from "antd";
import Loading from "./Loading";
import { GET_OPTIONS } from "../api/spending";
import useNoteSuggestions from "../hooks/useNoteSuggestions";
import useDefaultCurrencyId from "../hooks/useDefaultCurrencyId";

function SpendingForm({ initialValues, uploading, onSubmit }, ref) {
  const { loading, error, data } = useQuery(GET_OPTIONS);

  // 追蹤目前選到的分類，讓「詳細描述」能依分類帶出歷史備註建議
  const [categoryId, setCategoryId] = useState(initialValues.categoryId);
  const noteSuggestions = useNoteSuggestions(categoryId);

  // 新增消費時，幣別預設帶最近一筆消費的幣別（編輯時沿用原值，不查詢）。
  // Form 的 initialValues 只在首次 render 生效，所以要等這個查詢完成才能渲染表單。
  const { loading: defaultCurrencyLoading, currencyId: defaultCurrencyId } = useDefaultCurrencyId(
    initialValues.currencyId !== undefined
  );

  if (loading || defaultCurrencyLoading) return <Loading />;
  if (error) return <div>載入表單資料失敗，請稍後再試。</div>;

  const { currencies, categories } = data;
  const expenseCategories = categories.filter((category) => category.type !== "income");
  const incomeCategories = categories.filter((category) => category.type === "income");

  return (
    <Form
      ref={ref}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
      initialValues={{ currencyId: defaultCurrencyId, ...initialValues }}
      onFinish={onSubmit}
      onValuesChange={(changedValues) => {
        if ("categoryId" in changedValues) {
          setCategoryId(changedValues.categoryId);
        }
      }}
    >
      <Form.Item
        label="日期"
        name="date"
        rules={[{ required: true, message: "Please input the date!" }]}
      >
        <DatePicker />
      </Form.Item>
      <Form.Item
        label="金額"
        name="amount"
        rules={[
          { required: true, message: "Please input the amount!" },
          {
            validator: (_, value) =>
              value > 0
                ? Promise.resolve()
                : Promise.reject(new Error("The amount should be greater then zero!")),
          },
        ]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="幣別"
        name="currencyId"
        rules={[{ required: true, message: "Please select the currency!" }]}
      >
        <Select>
          {currencies.map((currency) => (
            <Select.Option key={currency.id} value={currency.id}>
              {currency.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="分類"
        name="categoryId"
        rules={[{ required: true, message: "Please select the category!" }]}
      >
        <Select>
          {expenseCategories.length > 0 && (
            <Select.OptGroup label="支出">
              {expenseCategories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
          {incomeCategories.length > 0 && (
            <Select.OptGroup label="收入">
              {incomeCategories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
        </Select>
      </Form.Item>

      <Form.Item
        label="詳細描述"
        name="note"
        rules={[{ required: true, message: "Please input the description!" }]}
      >
        <AutoComplete
          options={noteSuggestions.map((note) => ({ value: note }))}
          filterOption={(inputValue, option) =>
            option.value.toLowerCase().includes(inputValue.toLowerCase())
          }
        >
          <Input.TextArea rows={3} />
        </AutoComplete>
      </Form.Item>

      <Form.Item
        wrapperCol={{
          xs: { offset: 0 },
          sm: { offset: 6 },
        }}
      >
        <Button type="primary" htmlType="submit" disabled={uploading}>
          {uploading ? "Uploading..." : "Submit"}
        </Button>
      </Form.Item>
    </Form>
  );
}

export default React.forwardRef(SpendingForm);

SpendingForm.defaultProps = {
  initialValues: {},
  uploading: false,
};

SpendingForm.propTypes = {
  initialValues: PropTypes.objectOf(PropTypes.any),
  uploading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};
